/**
 * Manifest Signature Verification
 * V1.2: Ed25519 signature verification using Web Crypto API
 *
 * Note: Web Crypto API supports Ed25519 in modern browsers (Chrome 113+, Edge 113+).
 * For older browsers, verification will gracefully fail with a warning.
 */
/**
 * Convert hex string to Uint8Array
 */
function hexToBytes(hex) {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }
    return bytes;
}
/**
 * Convert base64 string to Uint8Array
 */
function base64ToBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
/**
 * Check if Ed25519 is supported in the current environment
 */
async function isEd25519Supported() {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        return false;
    }
    try {
        // Try to import a dummy key to check support
        const testKey = new Uint8Array(32).fill(1);
        await crypto.subtle.importKey('raw', testKey, { name: 'Ed25519' }, false, ['verify']);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Verify an Ed25519 signature using Web Crypto API
 */
async function verifyEd25519(message, signature, publicKeyBytes) {
    // Import the public key
    // Cast to ArrayBuffer for TypeScript compatibility
    const publicKey = await crypto.subtle.importKey('raw', publicKeyBytes.buffer.slice(publicKeyBytes.byteOffset, publicKeyBytes.byteOffset + publicKeyBytes.byteLength), { name: 'Ed25519' }, false, ['verify']);
    // Verify the signature
    return crypto.subtle.verify('Ed25519', publicKey, signature.buffer.slice(signature.byteOffset, signature.byteOffset + signature.byteLength), message.buffer.slice(message.byteOffset, message.byteOffset + message.byteLength));
}
/**
 * Derive signature URL from manifest URL
 */
export function deriveSignatureUrl(manifestUrl) {
    if (manifestUrl.endsWith('.json')) {
        return manifestUrl.replace(/\.json$/, '.sig');
    }
    return `${manifestUrl}.sig`;
}
/**
 * Fetch signature from URL
 */
export async function fetchSignature(signatureUrl) {
    const response = await fetch(signatureUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch signature: HTTP ${response.status}`);
    }
    return (await response.text()).trim();
}
/**
 * Verify manifest signature
 *
 * @param manifestJson - The raw JSON string of the manifest
 * @param signatureBase64 - The base64-encoded Ed25519 signature
 * @param publicKeyHex - The hex-encoded public key (64 characters)
 * @returns VerificationResult indicating success or failure
 */
export async function verifyManifestSignature(manifestJson, signatureBase64, publicKeyHex) {
    // Validate inputs
    if (!manifestJson) {
        return { verified: false, skipped: false, error: 'Empty manifest' };
    }
    if (!signatureBase64) {
        return { verified: false, skipped: false, error: 'Empty signature' };
    }
    if (!publicKeyHex || publicKeyHex.length !== 64) {
        return {
            verified: false,
            skipped: false,
            error: `Invalid public key length: expected 64 hex chars, got ${publicKeyHex?.length || 0}`,
        };
    }
    // Check Ed25519 support
    const supported = await isEd25519Supported();
    if (!supported) {
        return {
            verified: false,
            skipped: true,
            error: 'Ed25519 not supported in this browser. Use Chrome 113+ or Edge 113+.',
            publicKey: publicKeyHex,
        };
    }
    try {
        // Convert inputs
        const messageBytes = new TextEncoder().encode(manifestJson);
        const signatureBytes = base64ToBytes(signatureBase64);
        const publicKeyBytes = hexToBytes(publicKeyHex);
        // Validate signature length (Ed25519 signatures are 64 bytes)
        if (signatureBytes.length !== 64) {
            return {
                verified: false,
                skipped: false,
                error: `Invalid signature length: expected 64 bytes, got ${signatureBytes.length}`,
                publicKey: publicKeyHex,
            };
        }
        // Validate public key length (Ed25519 public keys are 32 bytes)
        if (publicKeyBytes.length !== 32) {
            return {
                verified: false,
                skipped: false,
                error: `Invalid public key length: expected 32 bytes, got ${publicKeyBytes.length}`,
                publicKey: publicKeyHex,
            };
        }
        // Verify signature
        const isValid = await verifyEd25519(messageBytes, signatureBytes, publicKeyBytes);
        return {
            verified: isValid,
            skipped: false,
            error: isValid ? undefined : 'Signature verification failed',
            publicKey: publicKeyHex,
        };
    }
    catch (error) {
        return {
            verified: false,
            skipped: false,
            error: `Verification error: ${error instanceof Error ? error.message : String(error)}`,
            publicKey: publicKeyHex,
        };
    }
}
/**
 * Full verification flow: fetch signature and verify
 *
 * @param manifestUrl - URL of the manifest
 * @param manifestJson - The raw JSON string of the manifest
 * @param config - Registry configuration
 * @returns VerificationResult
 */
export async function verifyManifest(manifestUrl, manifestJson, config) {
    // Skip if configured to skip
    if (config.skipVerification) {
        return { verified: false, skipped: true };
    }
    // Skip if no public key provided and not required
    if (!config.publicKey && !config.requireSignature) {
        return { verified: false, skipped: true };
    }
    // Require public key if signature is required
    if (!config.publicKey) {
        return {
            verified: false,
            skipped: false,
            error: 'Public key required for signature verification',
        };
    }
    try {
        // Determine signature URL
        const signatureUrl = config.signatureUrl || deriveSignatureUrl(manifestUrl);
        // Fetch signature
        const signatureBase64 = await fetchSignature(signatureUrl);
        // Verify
        return verifyManifestSignature(manifestJson, signatureBase64, config.publicKey);
    }
    catch (error) {
        return {
            verified: false,
            skipped: false,
            error: `Failed to verify: ${error instanceof Error ? error.message : String(error)}`,
            publicKey: config.publicKey,
        };
    }
}
//# sourceMappingURL=verify.js.map